import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, BarChart3, Wallet, TrendingUp, X, Bot, LogOut, User, ShieldCheck, Trophy, Box, HelpCircle } from "lucide-react";
import { startTour } from "@/components/GuidedTour";
import { Input } from "@/components/ui/input";
import { searchStocks } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ThemePicker } from "@/components/ThemePicker";
import MobileMenu from "@/components/MobileMenu";
import { PreviewBadge } from "@/components/PreviewBadge";

const NAV_ITEMS = [
  { path: "/", label: "Portfolio", id: "markets", icon: TrendingUp },
  { path: "/crypto", label: "Crypto", icon: BarChart3 },
  { path: "/portfolio", label: "My Picks", id: "portfolio", icon: Wallet, hideForAdmin: true },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/visualization", label: "Visualization", icon: Box },
  { path: "/woodrow", label: "Woodrow", icon: Bot },
  { path: "/admin", label: "Admin", icon: ShieldCheck, adminOnly: true },
];

export default function Header() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isEmulating, getEmulatingFrom, exitEmulation } = useAuth();
  const emulating = isEmulating();

  const doSearch = useCallback(async (q) => {
    if (q.length < 1) { setResults([]); return; }
    try {
      const res = await searchStocks(q);
      setResults(res.data || []);
    } catch { setResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && document.activeElement.tagName !== "INPUT") {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === "Escape") { setShowSearch(false); setQuery(""); setResults([]); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (symbol) => {
    navigate(`/stock/${symbol}`);
    setShowSearch(false);
    setQuery("");
    setResults([]);
  };

  return (
    <>
      {/* Emulation banner */}
      {emulating && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-bt-gold text-bt-bg text-center py-1.5 flex items-center justify-center gap-3" data-testid="emulation-banner">
          <span className="font-mono text-xs font-medium">
            Viewing as <strong>{user?.email}</strong> — emulated by {getEmulatingFrom()}
          </span>
          <button
            onClick={exitEmulation}
            className="font-mono text-xs font-bold bg-bt-bg text-bt-gold px-3 py-0.5 hover:bg-bt-bg/80 transition-colors"
            data-testid="exit-emulation-btn"
          >
            Exit Emulation
          </button>
        </div>
      )}
      <header className={`glass-header fixed left-0 right-0 z-50 border-b border-bt-border-subtle ${emulating ? "top-8" : "top-0"}`} data-testid="main-header">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between h-16 px-4 md:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <div className="w-8 h-8 bg-bt-gold rounded-sm flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-bt-bg" />
          </div>
          <span className="font-heading text-xl font-semibold tracking-tight text-bt-text-primary hidden sm:block">
            Rockafeller Jones
          </span>
        </Link>
        <PreviewBadge />

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
          {NAV_ITEMS.filter(item => {
            if (item.adminOnly && user?.role !== "superadmin") return false;
            if (item.hideForAdmin && user?.role === "superadmin") return false;
            return true;
          }).map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.id || item.label.toLowerCase()}`}
                title={item.label}
                className={`flex items-center gap-2 px-2.5 min-[1440px]:px-4 py-2 text-sm font-body whitespace-nowrap transition-colors ${
                  active
                    ? "text-bt-gold bg-bt-surface-2"
                    : "text-bt-text-secondary hover:text-bt-text-primary hover:bg-bt-hover"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden min-[1440px]:inline">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Search */}
        <div className="relative flex items-center gap-2 md:gap-3 min-w-0 ml-2">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="shrink-0 flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-bt-border-subtle bg-bt-surface text-bt-text-secondary text-sm font-mono hover:border-bt-border-focus transition-colors"
            data-testid="search-toggle-btn"
          >
            <Search className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden min-[1440px]:inline">Search</span>
            <kbd className="hidden min-[1440px]:inline text-xs bg-bt-surface-2 px-1.5 py-0.5 text-bt-text-tertiary">/</kbd>
          </button>
          <div className="hidden md:block"><ThemePicker /></div>
          <button onClick={startTour} title="Take the tour" data-testid="tour-help-btn"
            className="hidden md:block shrink-0 p-1.5 border border-bt-border-subtle bg-bt-surface text-bt-text-secondary hover:text-bt-gold hover:border-bt-border-focus transition-colors">
            <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
          </button>

          {/* Mobile nav */}
          <div className="flex md:hidden items-center min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden " data-testid="mobile-nav">
            {NAV_ITEMS.filter(item => {
              if (item.adminOnly) return false;
              if (item.hideForAdmin && user?.role === "superadmin") return false;
              return true;
            }).map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.id || item.label.toLowerCase()}`}
                  className={`p-2 shrink-0 transition-colors ${active ? "text-bt-gold" : "text-bt-text-secondary"}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </Link>
              );
            })}
          </div>
          {user && <MobileMenu user={user} logout={logout} />}

          {/* User */}
          {user && (
            <div className="hidden md:flex items-center gap-2 pl-3 border-l border-bt-border-subtle ml-1">
              <Link to="/profile" className="flex items-center gap-1.5 px-2 py-1 hover:bg-bt-surface-2 transition-colors" data-testid="profile-link">
                <User className="w-3.5 h-3.5 text-bt-text-tertiary" strokeWidth={1.5} />
                <span className="hidden lg:inline font-body text-sm font-medium text-bt-gold max-w-[110px] xl:max-w-[200px] truncate" data-testid="user-email">{user.name || user.email}</span>
                {user.role === "superadmin" && (
                  <span className="text-[9px] font-mono px-1 py-0.5 bg-bt-gold/20 text-bt-gold border border-bt-gold/30">ADMIN</span>
                )}
              </Link>
              <button
                onClick={logout}
                className="p-1.5 text-bt-text-tertiary hover:text-bt-negative transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search Overlay */}
      {showSearch && (
        <div className="absolute top-16 left-0 right-0 bg-bt-surface border-b border-bt-border-subtle shadow-2xl z-50" data-testid="search-overlay">
          <div className="max-w-[600px] mx-auto p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bt-text-tertiary" strokeWidth={1.5} />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stocks by name or symbol..."
                className="pl-10 pr-10 bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono placeholder:text-bt-text-tertiary focus:border-bt-gold focus:ring-bt-gold"
                data-testid="stock-search-input"
              />
              <button onClick={() => { setShowSearch(false); setQuery(""); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-bt-text-tertiary hover:text-bt-text-primary">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 border border-bt-border-subtle bg-bt-surface-2 max-h-64 overflow-y-auto" data-testid="search-results">
                {results.map((r) => (
                  <button
                    key={r.symbol}
                    onClick={() => handleSelect(r.symbol)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-bt-hover transition-colors text-left"
                    data-testid={`search-result-${r.symbol}`}
                  >
                    <div>
                      <span className="font-mono text-sm font-medium text-bt-text-primary">{r.symbol}</span>
                      <span className="ml-3 text-sm text-bt-text-secondary">{r.name}</span>
                    </div>
                    <span className="text-xs font-mono text-bt-text-tertiary">{r.sector}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
}
